/**
 * <Term> - cross-module glossary hover card (Academy PLAN 8).
 *
 * Wraps a piece of jargon anywhere in Vera: hovering shows the OneVeracity
 * glossary definition, clicking through opens the full glossary. Terms come
 * from GET /api/academy/terms (published terms only, open to any
 * authenticated user, cached module-wide for the session), so modules can
 * define their jargon in place without importing Academy.
 *
 *   <Term slug="rbp">RBP</Term>
 *
 * Unknown slugs render children unchanged (no error, no decoration), so a
 * term can be retired without breaking the screens that referenced it.
 */

import { ReactNode, useEffect, useState } from 'react';
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import { bffFetch } from '../../api/bffFetch';
import { veracityColors } from '../tokens';

export interface GlossaryTerm {
  id: string;
  slug: string;
  term: string;
  expansion: string | null;
  shortDef: string;
  aliases: string[];
  tags: string[];
}

// Module-wide cache: the published-terms read is small, cacheable, and used
// by every <Term> on a page; one fetch per session is plenty.
let termsPromise: Promise<GlossaryTerm[]> | null = null;

export function fetchGlossaryTerms(): Promise<GlossaryTerm[]> {
  if (!termsPromise) {
    termsPromise = bffFetch('/api/academy/terms')
      .then(async (res) => {
        if (!res.ok) throw new Error(`terms fetch failed (${res.status})`);
        const body = (await res.json()) as { terms: GlossaryTerm[] };
        return body.terms;
      })
      .catch((err) => {
        termsPromise = null; // don't cache failures
        throw err;
      });
  }
  return termsPromise;
}

/** Invalidate the session cache (used by the glossary editor after saves). */
export function invalidateGlossaryTerms(): void {
  termsPromise = null;
}

export function useGlossaryTerms(): GlossaryTerm[] | null {
  const [terms, setTerms] = useState<GlossaryTerm[] | null>(null);
  useEffect(() => {
    let alive = true;
    fetchGlossaryTerms()
      .then((t) => alive && setTerms(t))
      .catch(() => alive && setTerms([]));
    return () => {
      alive = false;
    };
  }, []);
  return terms;
}

const useStyles = makeStyles({
  trigger: {
    borderBottom: `1px dotted ${veracityColors.veracityOrange}`,
    cursor: 'help',
    color: 'inherit',
  },
  surface: {
    maxWidth: '360px',
    padding: tokens.spacingVerticalM,
  },
  term: {
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.midnightCharcoal,
    marginBottom: '2px',
  },
  expansion: {
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    marginBottom: tokens.spacingVerticalXS,
  },
  def: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.5,
    color: veracityColors.midnightCharcoal,
  },
  link: {
    display: 'inline-block',
    marginTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.veracityOrange,
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
});

export interface TermProps {
  slug: string;
  children?: ReactNode;
}

export function Term({ slug, children }: TermProps) {
  const styles = useStyles();
  const terms = useGlossaryTerms();
  const term = terms?.find((t) => t.slug === slug) ?? null;

  // Unknown slug (or terms still loading): plain text, no decoration.
  if (!term) return <>{children ?? slug}</>;

  return (
    <Popover openOnHover withArrow mouseLeaveDelay={150} positioning="above">
      <PopoverTrigger disableButtonEnhancement>
        <span className={styles.trigger}>{children ?? term.term}</span>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.term}>{term.term}</div>
        {term.expansion && <div className={styles.expansion}>{term.expansion}</div>}
        <div className={styles.def}>{term.shortDef}</div>
        <Link className={styles.link} to={`/academy/glossary#${term.slug}`}>
          Open in glossary
        </Link>
      </PopoverSurface>
    </Popover>
  );
}

export default Term;
